/**
 * Stripe event parsing and webhook signatures. Pure: the Worker fetches and stores, this file only
 * names what an event is and whether Stripe signed it (PAY-011).
 *
 * PAY-002 looks the base price up by this key, not by a hard-coded amount, so a price change in
 * the Stripe dashboard does not need a deploy.
 */
import { z } from "zod";

export const STRIPE_PRICE_LOOKUP_KEY = "lattice_monthly";

/** Stripe allows this much clock skew on a signed webhook, in milliseconds. */
export const STRIPE_SIGNATURE_TOLERANCE_MS = 5 * 60 * 1000;

const Metadata = z.record(z.string(), z.string());

/** Stripe sends either an id string or an expanded object with `id`. */
const StripeId = z.union([
  z.string().min(1),
  z.object({ id: z.string().min(1) }).transform((row) => row.id),
]);

const PauseCollection = z.object({ resumes_at: z.number().optional() }).nullable().optional();

const StripeObject = z.object({
  id: z.string().min(1),
  object: z.string().optional(),
  customer: StripeId.optional(),
  subscription: StripeId.optional(),
  invoice: StripeId.optional(),
  amount_total: z.number().optional(),
  amount_paid: z.number().optional(),
  billing_reason: z.string().optional(),
  payment_status: z.string().optional(),
  status: z.string().optional(),
  cancel_at_period_end: z.boolean().optional(),
  current_period_end: z.number().optional(),
  pause_collection: PauseCollection,
  metadata: Metadata.optional(),
});

const StripeEventBody = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  data: z.object({ object: StripeObject }),
});

const StripePriceRow = z.object({
  id: z.string().min(1),
  unit_amount: z.number().int(),
  lookup_key: z.string(),
});

const StripePriceList = z.object({ data: z.array(StripePriceRow) });

const StripeSessionBody = z.object({
  id: z.string().min(1),
  url: z.string().min(1),
  customer: z.string().min(1).nullable().optional(),
  subscription: z.string().min(1).nullable().optional(),
  invoice: z.string().min(1).nullable().optional(),
  amount_total: z.number().int().nullable().optional(),
});

const StripePortalBody = z.object({
  id: z.string().min(1),
  url: z.string().min(1),
});

const StripeSubscriptionItems = z.object({
  items: z.object({ data: z.array(z.object({ id: z.string().min(1) })) }),
});

export interface StripePortalSession {
  readonly id: string;
  readonly url: string;
}

export interface StripeAddonLine {
  readonly name: string;
  readonly amountCents: number;
}

export interface StripeObjectIds {
  readonly id: string;
  readonly customerId: string | null;
  readonly subscriptionId: string | null;
  readonly invoiceId: string | null;
  readonly amountCents: number | null;
  readonly billingReason: string | null;
  readonly orderId: string | null;
  readonly paymentStatus: string | null;
  readonly status: string | null;
  readonly cancelAtPeriodEnd: boolean;
  readonly periodEndOn: string | null;
  readonly pauseResumesOn: string | null;
}

export interface StripeEvent {
  readonly id: string;
  readonly type: string;
  readonly object: StripeObjectIds;
}

export interface StripePrice {
  readonly id: string;
  readonly unitAmount: number;
}

export interface StripeCheckoutSession {
  readonly id: string;
  readonly url: string;
  readonly customerId: string | null;
  readonly subscriptionId: string | null;
  readonly invoiceId: string | null;
  readonly amountTotal: number | null;
}

function parsedJson(
  payload: string,
): { readonly ok: true; readonly value: unknown } | { readonly ok: false } {
  try {
    return { ok: true, value: JSON.parse(payload) };
  } catch {
    return { ok: false };
  }
}

function amountOf(object: z.infer<typeof StripeObject>): number | null {
  if (object.amount_total !== undefined) return object.amount_total;

  if (object.amount_paid !== undefined) return object.amount_paid;

  return null;
}

function dayFromUnix(unix: number | undefined): string | null {
  if (unix === undefined || !Number.isFinite(unix)) return null;

  return new Date(unix * 1000).toISOString().slice(0, 10);
}

function subscriptionIdOf(object: z.infer<typeof StripeObject>): string | null {
  if (object.subscription !== undefined) return object.subscription;

  return object.object === "subscription" ? object.id : null;
}

/** The domain event, or null when the payload is not a Stripe event we can apply. */
export function readStripeEvent(payload: string): StripeEvent | null {
  const raw = parsedJson(payload);

  if (!raw.ok) return null;
  const parsed = StripeEventBody.safeParse(raw.value);

  if (!parsed.success) return null;
  const object = parsed.data.data.object;

  return {
    id: parsed.data.id,
    type: parsed.data.type,
    object: {
      id: object.id,
      customerId: object.customer ?? null,
      subscriptionId: subscriptionIdOf(object),
      invoiceId: object.invoice ?? null,
      amountCents: amountOf(object),
      billingReason: object.billing_reason ?? null,
      orderId: object.metadata?.["order_id"] ?? null,
      paymentStatus: object.payment_status ?? null,
      status: object.status ?? null,
      cancelAtPeriodEnd: object.cancel_at_period_end === true,
      periodEndOn: dayFromUnix(object.current_period_end),
      pauseResumesOn: dayFromUnix(object.pause_collection?.resumes_at),
    },
  };
}

/** A Price list looked up by key (PAY-002), or null when Stripe did not return that key. */
export function readStripePrice(payload: string, lookupKey: string): StripePrice | null {
  const raw = parsedJson(payload);

  if (!raw.ok) return null;
  const parsed = StripePriceList.safeParse(raw.value);

  if (!parsed.success) return null;
  const row = parsed.data.data.find((item) => item.lookup_key === lookupKey);

  return row === undefined ? null : { id: row.id, unitAmount: row.unit_amount };
}

/** A Checkout Session create response, or null when the body is not one. */
export function readStripeCheckoutSession(payload: string): StripeCheckoutSession | null {
  const raw = parsedJson(payload);

  if (!raw.ok) return null;
  const parsed = StripeSessionBody.safeParse(raw.value);

  if (!parsed.success) return null;

  return {
    id: parsed.data.id,
    url: parsed.data.url,
    customerId: parsed.data.customer ?? null,
    subscriptionId: parsed.data.subscription ?? null,
    invoiceId: parsed.data.invoice ?? null,
    amountTotal: parsed.data.amount_total ?? null,
  };
}

/** A Billing Portal session create response, or null when the body is not one. */
export function readStripePortalSession(payload: string): StripePortalSession | null {
  const raw = parsedJson(payload);

  if (!raw.ok) return null;
  const parsed = StripePortalBody.safeParse(raw.value);

  if (!parsed.success) return null;

  return { id: parsed.data.id, url: parsed.data.url };
}

/** Item ids on a Subscription retrieve, or null when the body is not one. */
export function readStripeSubscriptionItemIds(payload: string): readonly string[] | null {
  const raw = parsedJson(payload);

  if (!raw.ok) return null;
  const parsed = StripeSubscriptionItems.safeParse(raw.value);

  if (!parsed.success) return null;

  return parsed.data.items.data.map((item) => item.id);
}

/**
 * Form body that keeps the first Stripe item (the base price) and replaces the rest with add-on
 * price_data lines. Stripe then prorates per its defaults (PAY-022).
 */
export function addonSyncBody(
  itemIds: readonly string[],
  addons: readonly StripeAddonLine[],
): string | null {
  const baseId = itemIds[0];

  if (baseId === undefined) return null;

  const fields = new URLSearchParams({ "items[0][id]": baseId });

  for (const [index, id] of itemIds.slice(1).entries()) {
    const at = String(index + 1);

    fields.set(`items[${at}][id]`, id);
    fields.set(`items[${at}][deleted]`, "true");
  }

  for (const [index, addon] of addons.entries()) {
    const at = String(itemIds.length + index);

    fields.set(`items[${at}][quantity]`, "1");
    fields.set(`items[${at}][price_data][currency]`, "usd");
    fields.set(`items[${at}][price_data][unit_amount]`, String(addon.amountCents));
    fields.set(`items[${at}][price_data][recurring][interval]`, "month");
    fields.set(`items[${at}][price_data][product_data][name]`, addon.name);
  }

  return fields.toString();
}

function hexOf(bytes: ArrayBuffer): string {
  return [...new Uint8Array(bytes)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function stripeSignatureHex(secret: string, signedPayload: string): Promise<string> {
  const key = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );

  return hexOf(await crypto.subtle.sign("HMAC", key, new TextEncoder().encode(signedPayload)));
}

function hexEqual(left: string, right: string): boolean {
  if (left.length !== right.length) return false;
  let mismatch = 0;

  for (let i = 0; i < left.length; i++) mismatch |= left.charCodeAt(i) ^ right.charCodeAt(i);

  return mismatch === 0;
}

export interface StripeSignature {
  readonly timestamp: number;
  readonly v1: readonly string[];
}

/** The `t` and `v1` values from a Stripe-Signature header, or null if it is not that header. */
export function readStripeSignature(header: string): StripeSignature | null {
  let timestamp: number | null = null;
  const v1: string[] = [];

  for (const part of header.split(",")) {
    const split = part.indexOf("=");
    const key = part.slice(0, split).trim();
    const value = part.slice(split + 1).trim();

    if (key === "t") {
      const parsed = Number(value);

      if (!Number.isInteger(parsed)) return null;
      timestamp = parsed;
    }

    if (key === "v1" && value !== "") v1.push(value);
  }

  if (timestamp === null || v1.length === 0) return null;

  return { timestamp, v1 };
}

/**
 * True when `header` is a current Stripe signature of `payload` under `secret`. A timestamp older
 * than five minutes, or a v1 that does not match, is refused.
 */
export async function stripeSignatureOk(
  payload: string,
  header: string,
  secret: string,
  nowMs: number,
): Promise<boolean> {
  const signature = readStripeSignature(header);

  if (!signature) return false;

  if (Math.abs(nowMs - signature.timestamp * 1000) > STRIPE_SIGNATURE_TOLERANCE_MS) return false;
  const expected = await stripeSignatureHex(secret, `${String(signature.timestamp)}.${payload}`);

  return signature.v1.some((got) => hexEqual(got, expected));
}

/** The Stripe-Signature header a test (or the fake) would send for this payload. */
export async function stripeSignatureHeader(
  payload: string,
  secret: string,
  timestamp: number,
): Promise<string> {
  const v1 = await stripeSignatureHex(secret, `${String(timestamp)}.${payload}`);

  return `t=${String(timestamp)},v1=${v1}`;
}
